import { useState } from 'react'
import { api, type Branch } from '../lib/api.ts'
import { Checkbox } from './Checkbox.tsx'
import { useConfirm, type ConfirmRequest } from './Confirm.tsx'
import { useToast } from './Toast.tsx'

/**
 * Whether we verify a branch's requisitions, on the branch's page and in the
 * branch list (#676).
 *
 * A checkbox that asks first, in both directions: off stops a pharmacy's
 * requisitions from being verified, on spends model on every new one, and
 * neither should follow from a stray click in a table. The question says what
 * happens; a second switch undoes it.
 *
 * Super admin only, as the screens that hold it are.
 */
export function BranchVerificationSwitch({
  branch,
  onSwitched,
  compact = false,
}: {
  branch: Branch
  onSwitched: (branch: Branch) => void
  /** For a table cell: the box and its state, without the sentence under it. */
  compact?: boolean
}) {
  const [busy, setBusy] = useState(false)
  const confirm = useConfirm()
  const toast = useToast()

  async function toggle(on: boolean) {
    if (!(await confirm(switchQuestion(branch, on)))) return
    setBusy(true)
    try {
      const r = await api.setBranchEnabled(branch.id, on)
      onSwitched(r.branch)
      toast.show(`${branchLabel(branch)}: conferência ${on ? 'ligada' : 'desligada'}`, {
        tone: 'success',
      })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao mudar a conferência', {
        tone: 'error',
      })
    } finally {
      setBusy(false)
    }
  }

  const box = (
    <Checkbox
      checked={branch.enabled}
      disabled={busy}
      label={
        compact ? (
          <>
            {branch.enabled ? 'Ligada' : 'Desligada'}
            {/* Heard, not shown: the row already names the branch on screen,
                but a box read alone has to say which branch it switches. */}
            <span className="sr-only">
              {`: conferência das requisições da filial ${branchLabel(branch)}`}
            </span>
          </>
        ) : (
          'Conferir as requisições desta filial'
        )
      }
      onChange={(event) => void toggle(event.target.checked)}
    />
  )

  if (compact) {
    /*
     * Inside a row that opens the branch: the click, and the space bar on the
     * box, stop here, or switching would also navigate away from the answer.
     */
    return (
      <span
        className="inline-flex"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {box}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      {box}
      <span className="text-tSmall text-text-weak">{switchedLine(branch)}</span>
    </div>
  )
}

/** How the branch is named in the question and the toast: as on its page. */
function branchLabel(branch: Pick<Branch, 'erpCode' | 'name'>): string {
  return `${branch.erpCode} · ${branch.name}`
}

/** What switching it `on` or off does, asked before it is done. */
export function switchQuestion(
  branch: Pick<Branch, 'erpCode' | 'name'>,
  on: boolean,
): ConfirmRequest {
  return on
    ? {
        title: `Conferir as requisições da filial ${branchLabel(branch)}?`,
        message:
          'A partir da próxima leitura, o que chegar desta filial é lido e conferido, com custo de modelo, e o que ficou parado na fila volta a andar. Requisições aprovadas enquanto ela estava desligada não são buscadas.',
        confirmLabel: 'Conferir',
      }
    : {
        title: `Parar de conferir a filial ${branchLabel(branch)}?`,
        message:
          'A partir da próxima leitura, as requisições desta filial deixam de ser lidas e conferidas, e nada novo dela entra na fila. O que já está na fila fica como está e volta a andar quando a filial for ligada de novo.',
        confirmLabel: 'Parar de conferir',
      }
}

/** Who switched it last, and when; or that nobody has. */
export function switchedLine(
  branch: Pick<Branch, 'enabled' | 'switchedBy' | 'switchedAt'>,
): string {
  const state = branch.enabled ? 'Ligada' : 'Desligada'
  if (branch.switchedBy === undefined) {
    return branch.enabled ? 'Ligada desde que chegou do ERP' : state
  }
  const at = branch.switchedAt === undefined ? undefined : new Date(branch.switchedAt)
  const when =
    at === undefined || Number.isNaN(at.getTime())
      ? ''
      : ` em ${at.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
  return `${state} por ${branch.switchedBy}${when}`
}
